/**
 * Test Result Modal Component - 3-Tier Progressive Disclosure
 *
 * Displays connection test results with structured account info.
 * Shows after OAuth completion or manual test.
 *
 * Tier Structure:
 * - Tier 1 (always visible): Platform, account name, status badge, environment
 * - Tier 2 (expandable): Account details (ID, contact, location, plan)
 * - Tier 3 (nested collapse): Technical details + raw response
 *
 * States:
 * - Loading: Spinner with "Verifying connection..."
 * - Success: Checkmark with 3-tier progressive disclosure
 * - Failure: Error with message (connection still saved)
 */

import { Modal, Result, Collapse, Spin, Typography, Space, Tag, Divider, Alert } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import type { Platform, TestConnectionResult, AccountSummary, ContactInfo, LocationInfo, PlanInfo } from '@/types/connection.types'

const { Text, Title, Paragraph } = Typography

/**
 * Props for TestResultModal component
 */
export interface TestResultModalProps {
  /** Modal visibility */
  open: boolean

  /** Close callback */
  onClose: () => void

  /** Test result (null while loading) */
  testResult: TestConnectionResult | null

  /** Platform being tested */
  platform: Platform | string

  /** Loading state */
  isLoading?: boolean
}

/**
 * Get platform display name
 */
function getPlatformDisplayName(platform: Platform | string): string {
  const platformNames: Record<string, string> = {
    quickbooks: 'QuickBooks',
    'quickbooks-sandbox': 'QuickBooks',
    square: 'Square',
    'square-sandbox': 'Square',
    shopify: 'Shopify',
    'shopify-sandbox': 'Shopify',
  }
  return platformNames[platform] || platform.split('-')[0]
}

/**
 * Check if any values exist in an object
 */
function hasAnyValue(obj?: Record<string, unknown> | ContactInfo | LocationInfo | PlanInfo): boolean {
  if (!obj) return false
  return Object.values(obj).some(v => v != null && v !== '')
}

/**
 * Get country display name from ISO code
 */
function getCountryName(code?: string): string {
  if (!code) return ''
  const countries: Record<string, string> = {
    US: 'United States',
    CA: 'Canada',
    GB: 'United Kingdom',
    AU: 'Australia',
    DE: 'Germany',
    FR: 'France',
    JP: 'Japan',
  }
  return countries[code] || code
}

/**
 * Get status badge color
 */
function getStatusColor(status: AccountSummary['status']): string {
  switch (status) {
    case 'active':
      return 'green'
    case 'inactive':
      return 'red'
    case 'trial':
      return 'blue'
    case 'suspended':
      return 'orange'
    default:
      return 'default'
  }
}

/**
 * Format status for display
 */
function formatStatus(status: AccountSummary['status']): string {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

/**
 * Tier 1 Component - Always Visible Identity + Status
 */
const Tier1Header: React.FC<{ accountSummary: AccountSummary; platform: string }> = ({
  accountSummary,
  platform,
}) => {
  const platformName = getPlatformDisplayName(platform)

  return (
    <div style={{ textAlign: 'center', marginBottom: 16 }}>
      <Title level={4} style={{ marginBottom: 4 }}>
        {platformName} - {accountSummary.accountName}
      </Title>
      <Space size="middle">
        <Tag color={accountSummary.environment === 'production' ? 'blue' : 'orange'}>
          {accountSummary.environment === 'production' ? 'Production' : 'Sandbox'}
        </Tag>
        <Tag color={getStatusColor(accountSummary.status)}>
          {formatStatus(accountSummary.status)}
        </Tag>
      </Space>
    </div>
  )
}

/**
 * Detail Row Component - For Tier 2 details
 */
const DetailRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
  if (!value) return null
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
      <Text type="secondary">{label}:</Text>
      <Text>{value}</Text>
    </div>
  )
}

/**
 * Tier 2 Component - Expandable Account Details
 */
const Tier2AccountDetails: React.FC<{ accountSummary: AccountSummary }> = ({ accountSummary }) => {
  const hasDetails =
    accountSummary.accountId ||
    accountSummary.domain ||
    hasAnyValue(accountSummary.contact) ||
    hasAnyValue(accountSummary.location) ||
    hasAnyValue(accountSummary.plan)

  if (!hasDetails) return null

  return (
    <Collapse
      ghost
      style={{ marginTop: 8 }}
      items={[
        {
          key: 'account-details',
          label: <Text type="secondary">Account Details</Text>,
          children: (
            <div
              style={{
                backgroundColor: '#fafafa',
                borderRadius: 8,
                padding: 16,
              }}
            >
              {/* Account ID */}
              <DetailRow
                label="Account ID"
                value={accountSummary.accountId}
              />

              {/* Domain (Shopify) */}
              <DetailRow label="Domain" value={accountSummary.domain} />

              {/* Contact Section */}
              {hasAnyValue(accountSummary.contact) && (
                <>
                  <Divider style={{ margin: '8px 0' }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Contact
                  </Text>
                  <DetailRow label="Email" value={accountSummary.contact?.email} />
                  <DetailRow label="Phone" value={accountSummary.contact?.phone} />
                  <DetailRow label="Address" value={accountSummary.contact?.address} />
                </>
              )}

              {/* Location Section */}
              {hasAnyValue(accountSummary.location) && (
                <>
                  <Divider style={{ margin: '8px 0' }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Location
                  </Text>
                  <DetailRow
                    label="Country"
                    value={getCountryName(accountSummary.location?.country)}
                  />
                  <DetailRow label="Currency" value={accountSummary.location?.currency} />
                  <DetailRow label="Timezone" value={accountSummary.location?.timezone} />
                </>
              )}

              {/* Plan Section */}
              {hasAnyValue(accountSummary.plan) && (
                <>
                  <Divider style={{ margin: '8px 0' }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Plan
                  </Text>
                  <DetailRow label="Plan" value={accountSummary.plan?.name} />
                  <DetailRow label="Type" value={accountSummary.plan?.type} />
                </>
              )}
            </div>
          ),
        },
      ]}
    />
  )
}

/**
 * Tier 3 Component - Technical Details with Raw Response
 */
const Tier3TechnicalDetails: React.FC<{
  testResult: TestConnectionResult
}> = ({ testResult }) => {
  return (
    <Collapse
      ghost
      style={{ marginTop: 8 }}
      items={[
        {
          key: 'technical',
          label: (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Technical Details
            </Text>
          ),
          children: (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Connection UID: {testResult.connectionUid}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Response Time: {testResult.responseTimeMs.toFixed(0)}ms
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Tested At: {new Date(testResult.testedAt).toLocaleString()}
              </Text>

              {/* Raw Response - Tier 3 nested */}
              {testResult.rawResponse && (
                <Collapse
                  ghost
                  size="small"
                  items={[
                    {
                      key: 'raw',
                      label: (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Raw Account Info
                        </Text>
                      ),
                      children: (
                        <pre
                          style={{
                            fontSize: 10,
                            backgroundColor: '#f5f5f5',
                            padding: 8,
                            borderRadius: 4,
                            overflow: 'auto',
                            maxHeight: 200,
                          }}
                        >
                          {JSON.stringify(testResult.rawResponse, null, 2)}
                        </pre>
                      ),
                    },
                  ]}
                />
              )}
            </Space>
          ),
        },
      ]}
    />
  )
}

/**
 * Test Result Modal
 *
 * Displays merchant info after connection test with 3-tier progressive disclosure.
 */
export const TestResultModal: React.FC<TestResultModalProps> = ({
  open,
  onClose,
  testResult,
  platform,
  isLoading = false,
}) => {
  const platformName = getPlatformDisplayName(platform)

  // Loading state
  if (isLoading) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={520}
        centered
        closable={false}
        maskClosable={false}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px 24px',
          }}
        >
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            size="large"
          />
          <Paragraph style={{ marginTop: 24, fontSize: 16 }}>
            Verifying connection with {platformName}...
          </Paragraph>
          <Text type="secondary">This may take a few seconds</Text>
        </div>
      </Modal>
    )
  }

  // No result yet
  if (!testResult) {
    return null
  }

  // Success state with 3-tier progressive disclosure
  if (testResult.healthy && testResult.accountSummary) {
    const { accountSummary } = testResult
    const isSandbox = accountSummary.environment === 'sandbox'

    return (
      <Modal
        open={open}
        onCancel={onClose}
        onOk={onClose}
        okText="Done"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={520}
        centered
      >
        <Result
          status="success"
          icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
          title="Connection Verified"
          subTitle={`Successfully connected to your ${platformName} account`}
        />

        {/* Tier 1 - Always visible identity */}
        <Tier1Header accountSummary={accountSummary} platform={platform} />

        {/* Tier 2 - Expandable account details */}
        <Tier2AccountDetails accountSummary={accountSummary} />

        {/* Tier 3 - Technical details */}
        <Tier3TechnicalDetails testResult={testResult} />

        {/* Sandbox Warning */}
        {isSandbox && (
          <Alert
            type="warning"
            icon={<WarningOutlined />}
            message="Sandbox Environment"
            description="This connection uses test credentials. Data will not sync with your production account."
            style={{ marginTop: 16 }}
          />
        )}
      </Modal>
    )
  }

  // Failure state
  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      okText="Close"
      cancelButtonProps={{ style: { display: 'none' } }}
      width={520}
      centered
    >
      <Result
        status="error"
        icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        title="Connection Test Failed"
        subTitle={
          <Space direction="vertical" size="small" style={{ textAlign: 'center' }}>
            <Text>
              We saved your connection, but couldn't verify it with {platformName}.
            </Text>
            <Text type="secondary">
              {testResult.errorMessage || 'This might be temporary. You can test again later.'}
            </Text>
          </Space>
        }
      />

      <Tier3TechnicalDetails testResult={testResult} />
    </Modal>
  )
}

export default TestResultModal
